import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearAuditChanged } from '../../redux/reducer/coreScriptReducer';
import { RootState } from '../../redux/Store';
import { contextPostMessageBack, trustedReturnDomains } from '../../constants';
import { logErrorToDB } from './logErrorToDb';

// Returns the origin only if it is https and its hostname matches one of the
// trusted base domains (exact, or as a `.<domain>` suffix). This accepts any
// subdomain prefix (spiglass, spiglass2, spiglass-beta, ...) without listing
// them, while refusing untrusted origins. Returns '' when not trustworthy.
const validateTrustedOrigin = (origin: string | undefined | null): string => {
  if (!origin) {
    return '';
  }
  try {
    const url = new URL(origin);
    if (url.protocol !== 'https:') {
      return '';
    }
    const host = url.hostname;
    const isTrusted = trustedReturnDomains.some(
      (domain) => host === domain || host.endsWith(`.${domain}`)
    );
    return isTrusted ? url.origin : '';
  } catch {
    return '';
  }
};

// Resolves the calling site's true origin from the browser, in priority order:
//   1. ancestorOrigins[0] — exact parent origin for iframes (Chromium/WebKit)
//   2. document.referrer  — opener's page (popups) / Firefox iframe fallback
// Each candidate is validated against the trusted-domain allowlist. Returns ''
// when no trusted origin can be resolved.
const resolveCallerOrigin = (): string => {
  const ancestorOrigin = validateTrustedOrigin(
    globalThis.location.ancestorOrigins?.[0]
  );
  if (ancestorOrigin) {
    return ancestorOrigin;
  }

  if (document.referrer) {
    try {
      return validateTrustedOrigin(new URL(document.referrer).origin);
    } catch {
      return '';
    }
  }

  return '';
};

export const handleSendMessageStatus = (data?: {
  SubmitComplete: boolean; //script is complete script was completed with all required fields
  Followup: boolean; //Audit Results Sending on Followup
  AuditSaved: string | number | undefined; //Relevant to submitting audit results context
  Canceled: boolean; //Canceled close without saving
  Context: string;
  ContextKey: string;
  ContextId: string;
  Message: string | undefined;
  MessageType: number | undefined;
}) => {
  if (!data || !data.Context) {
    return;
  }
  const supportMessageBack = contextPostMessageBack[data.Context] ?? false;
  if (!supportMessageBack) {
    return;
  }
  // The calling site embeds us as an iframe (window.parent) or launches us as
  // a popup (window.opener). Prefer an opener, fall back to a real parent, and
  // bail when neither exists (standalone / full-page navigation, out of scope).
  const isFramed = globalThis.self !== globalThis.top;
  const targetWindow = window.opener ?? (isFramed ? window.parent : null);
  if (!targetWindow) {
    return;
  }

  // Post only to the calling site's real, trusted origin. Never guess and
  // never use '*'. If the origin can't be resolved/validated, do not post.
  const targetOrigin = resolveCallerOrigin();
  if (!targetOrigin) {
    logErrorToDB(
      new Error('usePostMessage: unable to resolve a trusted caller origin'),
      {
        context: data.Context,
        ancestorOrigin: globalThis.location.ancestorOrigins?.[0] ?? null,
        referrer: document.referrer || null,
      }
    );
    return;
  }

  targetWindow.postMessage(data, targetOrigin);

  return;
};

const useContextQueryObj = () => {
  return useSelector((state: RootState) => state.contextId.queryObj);
};
export const useSinglePostMessage = (stateData: any) => {
  const hasSentMessage = useRef(false);
  const prevAuditChanged = useRef(stateData?.AuditChanged);
  const dispatch = useDispatch();
  const queryObj = useContextQueryObj();

  useEffect(() => {
    const supportMessageBack =
      contextPostMessageBack[queryObj?.context] ?? false;
    if (!supportMessageBack) {
      return;
    }

    // Check if AuditChanged has changed
    if (stateData?.AuditChanged !== prevAuditChanged.current) {
      // Update the ref to the current value
      prevAuditChanged.current = stateData?.AuditChanged;

      if (stateData?.AuditChanged && !hasSentMessage.current) {
        handleSendMessageStatus({
          Canceled: false,
          SubmitComplete: false,
          AuditSaved: stateData?.AuditSaved,
          Followup: stateData?.Followup,
          Context: `${queryObj?.context}`,
          ContextId: `${queryObj?.id}`,
          ContextKey: `${queryObj?.key}`,
          Message: undefined,
          MessageType: undefined,
        });
        dispatch(clearAuditChanged());
        hasSentMessage.current = true;
      }

      if (!stateData?.AuditChanged) {
        hasSentMessage.current = false;
      }
    }
  }, [
    stateData?.AuditChanged,
    dispatch,
    stateData,
    queryObj?.context,
    queryObj?.id,
    queryObj?.key,
  ]);
};

export const cancelPostMessage = (context: string, key: string, id: string) => {
  const supportMessageBack = contextPostMessageBack[context] ?? false;
  if (!supportMessageBack) {
    return;
  }

  handleSendMessageStatus({
    Canceled: true,
    SubmitComplete: false, //relevant to saving
    AuditSaved: undefined, //Relevant to submit audited context only
    Followup: false, //Relevant to submit audited context only
    Context: context,
    ContextId: id,
    ContextKey: key,
    Message: undefined, //no save message on cancel
    MessageType: undefined,
  });
};
// export default useSinglePostMessage;

// export default handleSendMessageStatus;
